import React, { useEffect, useState } from 'react';
import { StudyOffer } from '@/services/offers/StudyOffersService';
import Button from '@/components/ui/Button';
import { MapPin, Clock, Check, X, ExternalLink } from 'lucide-react';

interface Props {
  offer: StudyOffer;
  onAccept: (id: number) => void;
  getDistanceKm?: (offer: StudyOffer) => Promise<number | null>;
}

const StudyOfferCard: React.FC<Props> = ({ offer, onAccept, getDistanceKm }) => {
  const [distance, setDistance] = useState<number | null>(null);

  useEffect(() => {
    let mounted = true;
    if (getDistanceKm) {
      getDistanceKm(offer).then((km) => { if (mounted) setDistance(km); });
    }
    return () => { mounted = false; };
  }, [offer, getDistanceKm]);

  const mapsUrl = (() => {
    if (offer.lat != null && offer.lng != null) {
      return `https://www.google.com/maps/dir/?api=1&destination=${offer.lat},${offer.lng}`;
    }
    if (offer.address) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(offer.address)}`;
    }
    return 'https://www.google.com/maps';
  })();

  const exp = new Date(offer.expires_at);
  const offered = new Date(offer.offered_at);

  return (
    <div className="p-3 rounded-md border bg-white">
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {offer.contact_name || `Contato #${offer.contact_id}`}
          </p>
          {offer.address && (
            <p className="mt-1 text-sm text-gray-700 flex items-center gap-1">
              <MapPin className="h-4 w-4 text-gray-500" />
              <span className="truncate" title={offer.address}>{offer.address}</span>
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>Ofertado: {offered.toLocaleString()} • Expira: {exp.toLocaleTimeString()}</span>
          </p>
          {distance != null && (
            <p className="mt-1 text-xs text-gray-600">Distância: {distance.toFixed(1)} km</p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs">
            <ExternalLink className="h-4 w-4" /> Ver rota
          </a>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onAccept(offer.id)}>
              <Check className="h-4 w-4 mr-1" /> Aceitar
            </Button>
            {/* Placeholder to potentially support Recusar futuramente */}
            <Button variant="secondary" size="sm">
              <X className="h-4 w-4 mr-1" /> Recusar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyOfferCard;
